import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { COLOR_META, pieceBoardPoint } from '@shared/game/board';
import type { GameAction, GameAnimation, GameState, SeatColor } from '@shared/game/types';
import { Icon } from './Icon';

interface EffectCinematicProps {
  action: GameAction | null;
  game: GameState;
  enabled: boolean;
  astral?: boolean;
}

function percent(x: number, y: number): CSSProperties {
  return { left: `${x / 10}%`, top: `${y / 10}%` };
}

function colorClass(color: SeatColor): string {
  return `cinematic--${color}`;
}

export function EffectCinematic({ action, game, enabled, astral = false }: EffectCinematicProps) {
  const [animation, setAnimation] = useState<GameAnimation | null>(null);
  const [phase, setPhase] = useState(0);
  const [sceneKey, setSceneKey] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    for (const timer of timers.current) window.clearTimeout(timer);
    timers.current = [];
  };

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setAnimation(null);
      return undefined;
    }
    const next = action?.animation;
    // Una tirada posterior puede llegar mientras la escena sigue en pantalla.
    // No la cortamos: cada cinemática termina su propio tiempo de lectura.
    if (!next) return undefined;
    clearTimers();
    setAnimation(next);
    setPhase(0);
    setDismissed(false);
    setSceneKey((key) => key + 1);
    timers.current = [
      window.setTimeout(() => setPhase(1), 420),
      window.setTimeout(() => setPhase(2), 1350),
      window.setTimeout(() => setAnimation(null), 2700),
    ];
    return undefined;
  }, [action, enabled]);

  useEffect(() => () => clearTimers(), []);

  if (!animation || dismissed) return null;

  const player = game.players.find((candidate) => candidate.id === action?.playerId);
  const color = animation.color ?? player?.color ?? 'coral';
  const from = animation.from ?? 0;
  const to = animation.to ?? from;
  const toPoint = pieceBoardPoint(color, to, 0, 1);
  // La escena principal se representa en el centro de la arena para que siga
  // siendo legible aunque la celda de origen quede junto al borde del tablero.
  const stage = percent(500, 500);
  const defenderColor = animation.captured?.[0]?.color ?? color;
  const defender = game.players.find((candidate) => candidate.color === defenderColor);
  const attackerName = player?.name ?? COLOR_META[color].house;
  const defenderName = defender?.name ?? COLOR_META[defenderColor].house;
  const reward = animation.reward ?? 20;
  const label =
    animation.kind === 'portal'
      ? 'Portal de salto'
      : animation.kind === 'crack'
        ? 'Anomalía de fisura'
        : animation.kind === 'capture'
          ? 'Pulso de captura'
          : animation.kind === 'summon'
            ? 'Despliegue de unidad'
            : animation.kind === 'goal'
              ? 'Núcleo encendido'
              : animation.kind === 'shield'
                ? 'Barrera de fase'
                : animation.kind === 'wait'
                  ? 'Bucle de estasis'
                  : animation.kind === 'extra'
                    ? 'Sobreimpulso'
                    : 'Cofre de anomalías';

  return (
    <div
      key={sceneKey}
      className={`cinematic ${colorClass(color)} cinematic--${animation.kind} ${astral && animation.kind === 'capture' ? 'cinematic--astral' : ''}`}
      role={animation.kind === 'capture' ? 'dialog' : undefined}
      aria-label={animation.kind === 'capture' ? 'Escena de captura' : undefined}
    >
      <div className="cinematic__vignette" />
      {animation.kind === 'capture' && (
        <button
          type="button"
          className="cinematic__skip"
          onClick={() => setDismissed(true)}
          aria-label="Saltar escena de captura"
        >
          <Icon name="close" size={14} /> Saltar escena
        </button>
      )}
      {(animation.kind === 'portal' || animation.kind === 'summon') && (
        <>
          <div className="cinematic__portal" style={stage}>
            <span className="cinematic__portal-ring cinematic__portal-ring--one" />
            <span className="cinematic__portal-ring cinematic__portal-ring--two" />
            <span className="cinematic__portal-core" />
          </div>
          <span
            className={`cinematic__ghost ${phase >= 1 ? 'is-travelling' : ''}`}
            style={{
              ...stage,
              ...(phase >= 1 ? percent(toPoint.x, toPoint.y) : {}),
            }}
          >
            <i />
          </span>
          <span className={`cinematic__arrival ${phase >= 1 ? 'is-open' : ''}`} style={percent(toPoint.x, toPoint.y)}>
            <i />
            <i />
            <i />
          </span>
        </>
      )}

      {animation.kind === 'crack' && (
        <>
          <div className={`cinematic__crack ${phase >= 1 ? 'is-open' : ''}`} style={stage}>
            <svg viewBox="-60 -60 120 120" width="100%" height="100%">
              <path className="crack-line crack-line--one" d="M-5-56 10-22-8-2 14 24-4 55" />
              <path className="crack-line crack-line--two" d="M8-21 34-30M-6 0-34 10M11 23 36 38" />
              <path className="crack-line crack-line--three" d="M-4 52-22 63M13 26 31 21" />
            </svg>
            <span className="cinematic__crack-glow" />
          </div>
          <span className={`cinematic__ghost is-falling ${phase >= 1 ? 'is-fallen' : ''}`} style={stage}>
            <i />
          </span>
          <span className={`cinematic__arrival ${phase >= 2 ? 'is-open' : ''}`} style={percent(toPoint.x, toPoint.y)}>
            <i />
            <i />
            <i />
          </span>
        </>
      )}

      {animation.kind === 'capture' && (
        <>
          <div className="cinematic__impact" style={stage}>
            <span className="cinematic__impact-ring" />
            <span className="cinematic__slash cinematic__slash--one" />
            <span className="cinematic__slash cinematic__slash--two" />
          </div>
          <div className={`cinematic__capture-title ${phase >= 1 ? 'is-visible' : ''}`}>
            <span>CAPTURA</span>
            <small>
              {(animation.captured?.length ?? 0)} unidad{(animation.captured?.length ?? 0) === 1 ? '' : 'es'} devuelta{(animation.captured?.length ?? 0) === 1 ? '' : 's'} al muelle
            </small>
          </div>
          <div className={`cinematic__battle ${phase >= 1 ? 'is-fighting' : ''}`}>
            <div className={`cinematic__army cinematic__army--attacker cinematic__army--${color}`}>
              <span className="cinematic__army-name">{attackerName}</span>
              <div className="cinematic__soldiers"><i /><i /><i /></div>
            </div>
            <div className="cinematic__volley" aria-hidden="true">
              <i /><i /><i /><i />
            </div>
            <div className={`cinematic__army cinematic__army--defender cinematic__army--${defenderColor}`}>
              <span className="cinematic__army-name">{defenderName}</span>
              <div className="cinematic__soldiers"><i /><i /><i /></div>
            </div>
          </div>
          <div className={`cinematic__reward ${phase >= 1 ? 'is-visible' : ''}`}>
            <strong>+{reward}</strong>
            <span>puntos de captura</span>
          </div>
          {(animation.captured ?? []).map((piece, index) => {
            const start = pieceBoardPoint(piece.color, piece.progress, 0, 1);
            const base = pieceBoardPoint(piece.color, -1, index, 1);
            return (
              <span
                key={piece.pieceId}
                className={`cinematic__captured cinematic__captured--${piece.color} ${phase >= 1 ? 'is-flying' : ''}`}
                style={{
                  ...(phase >= 1 ? percent(base.x, base.y) : percent(start.x, start.y)),
                  transitionDelay: `${index * 70}ms`,
                }}
              >
                <i />
              </span>
            );
          })}
        </>
      )}

      {(animation.kind === 'shield' || animation.kind === 'wait' || animation.kind === 'extra' || animation.kind === 'rune') && (
        <div className={`cinematic__sigil cinematic__sigil--${animation.kind}`} style={stage}>
          <span />
          <span />
          <Icon name={animation.kind === 'shield' ? 'shield' : animation.kind === 'wait' ? 'moon' : 'spark'} size={27} />
        </div>
      )}

      {animation.kind === 'goal' && (
        <div className="cinematic__goal" style={percent(500, 500)}>
          <span className="cinematic__goal-ring" />
          <span className="cinematic__goal-ring cinematic__goal-ring--two" />
          <Icon name="crown" size={48} />
        </div>
      )}

      <div className={`cinematic__caption ${phase >= 1 ? 'is-visible' : ''}`} style={{ borderColor: `var(--${color})` }}>
        <span className="cinematic__caption-mark">
          <Icon name="spark" size={15} />
        </span>
        <span>
          <strong>{label}</strong>
          <small>
            {animation.kind === 'portal' || animation.kind === 'crack'
              ? `Ruta ${from} → ${to} · ${COLOR_META[color].house}`
              : `${COLOR_META[color].house} · evento autoritativo`}
          </small>
        </span>
      </div>
    </div>
  );
}
